import React, { useRef, useState } from 'react';
import { Avatar, Box, Button, CircularProgress, IconButton, Typography, alpha } from '@mui/material';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import proxy from '../api/axios';
import { resolveProfileImage, syncSessionUser } from '../common/utils';
import { updateUserInfo } from '../redux/userSlice';

const ACCENT = '#137274';

/**
 * Compact avatar editor for own profile — upload / replace / remove.
 */
const ProfileAvatarEditor = ({
  name,
  profileImage,
  initials,
  size = 108,
  isDark,
  muted,
  onUpdated,
}) => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const displaySrc = preview || resolveProfileImage(profileImage);

  const applyUser = (user) => {
    if (!user) return;
    const next = syncSessionUser({
      name: user.name,
      username: user.username,
      email: user.email,
      profileImage: user.profileImage || null,
      role: user.role,
      isPremiumUser: user.isPremiumUser,
      points: user.points,
      totalSolvedProblems: user.totalSolvedProblems,
      _id: user._id,
    });
    dispatch(updateUserInfo(next));
    onUpdated?.(user);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be 2MB or smaller');
      return;
    }

    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);
    setBusy(true);
    try {
      const form = new FormData();
      form.append('avatar', file);
      const { data } = await proxy.post('/users/profile/avatar', form);
      toast.success(data.message || 'Profile picture updated');
      applyUser(data.user);
      setPreview(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to upload picture');
      setPreview(null);
    } finally {
      URL.revokeObjectURL(localUrl);
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!profileImage && !preview) return;
    setBusy(true);
    try {
      const { data } = await proxy.delete('/users/profile/avatar');
      toast.success(data.message || 'Profile picture removed');
      applyUser(data.user);
      setPreview(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove picture');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1 }}>
      <Box sx={{ position: 'relative', width: size, height: size }}>
        <Avatar
          alt={name}
          src={displaySrc}
          sx={{
            width: size,
            height: size,
            fontSize: size > 80 ? 40 : 28,
            fontWeight: 800,
            bgcolor: alpha(ACCENT, isDark ? 0.35 : 0.18),
            color: isDark ? '#7ed4d4' : ACCENT,
            border: `3px solid ${isDark ? alpha('#fff', 0.12) : '#fff'}`,
            boxShadow: `0 12px 32px ${alpha(ACCENT, 0.2)}`,
          }}
        >
          {initials}
        </Avatar>
        {busy && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              bgcolor: alpha('#000', 0.45),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={28} sx={{ color: '#fff' }} />
          </Box>
        )}
        <IconButton
          size="small"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          aria-label="Change profile picture"
          sx={{
            position: 'absolute',
            right: 2,
            bottom: 2,
            bgcolor: ACCENT,
            color: '#fff',
            width: 34,
            height: 34,
            boxShadow: `0 4px 12px ${alpha(ACCENT, 0.4)}`,
            '&:hover': { bgcolor: '#0f5c5e' },
            '&.Mui-disabled': { bgcolor: alpha(ACCENT, 0.5), color: '#fff' },
          }}
        >
          <PhotoCameraOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          hidden
          onChange={handleFile}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        <Button
          size="small"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          sx={{ textTransform: 'none', fontWeight: 700, color: ACCENT, minWidth: 0 }}
        >
          {profileImage ? 'Change photo' : 'Upload photo'}
        </Button>
        {!!profileImage && (
          <Button
            size="small"
            disabled={busy}
            startIcon={<DeleteOutlineIcon sx={{ fontSize: '16px !important' }} />}
            onClick={handleRemove}
            sx={{ textTransform: 'none', fontWeight: 700, color: muted, minWidth: 0 }}
          >
            Remove
          </Button>
        )}
      </Box>
      <Typography variant="caption" sx={{ color: muted, maxWidth: 200, textAlign: { xs: 'left', sm: 'center' } }}>
        JPEG, PNG, WebP or GIF · max 2MB
      </Typography>
    </Box>
  );
};

export default ProfileAvatarEditor;
